import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

const PRESET_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL']

const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  stock: '',
  available: true,
  category_id: '',
  type_id: '',
}

export default function ProductForm({ product, onClose }) {
  const [formData, setFormData] = useState(
    product ? {
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      stock: product.stock || '',
      available: product.available ?? true,
      category_id: product.category_id || '',
      type_id: product.type_id || '',
    } : EMPTY_FORM
  )
  const [categories, setCategories] = useState([])
  const [types, setTypes] = useState([])
  const [imageUrl, setImageUrl] = useState(product?.image_url || null)
  const [sizes, setSizes] = useState(product?.sizes || [])
  const [customSize, setCustomSize] = useState('')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    supabase.from('product_categories').select('*').eq('club_id', CLUB_ID).order('name')
      .then(({ data }) => { if (data) setCategories(data) })
  }, [])

  useEffect(() => {
    if (!formData.category_id) { setTypes([]); return }
    supabase.from('product_types').select('*').eq('category_id', formData.category_id).order('name')
      .then(({ data }) => { if (data) setTypes(data) })
  }, [formData.category_id])

  // ── Size helpers ────────────────────────────────────────────────────────────
  const hasSize = (size) => sizes.some(s => s.size === size)

  const togglePresetSize = (size) => {
    if (hasSize(size)) {
      setSizes(prev => prev.filter(s => s.size !== size))
    } else {
      setSizes(prev => [...prev, { size, stock: 0 }])
    }
  }

  const updateSizeStock = (size, stock) => {
    setSizes(prev => prev.map(s => s.size === size ? { ...s, stock: stock === '' ? '' : stock } : s))
  }

  const addCustomSize = () => {
    const trimmed = customSize.trim().toUpperCase()
    if (!trimmed || hasSize(trimmed)) { setCustomSize(''); return }
    setSizes(prev => [...prev, { size: trimmed, stock: 0 }])
    setCustomSize('')
  }

  const removeSize = (size) => setSizes(prev => prev.filter(s => s.size !== size))

  // ── Image upload ────────────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return }
    if (file.size > 3 * 1024 * 1024) { setError('Image must be under 3MB'); return }
    const localPreview = URL.createObjectURL(file)
    setImageUrl(localPreview)
    setUploading(true); setError(null)
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`
    const { error: uploadError } = await supabase.storage.from('product-images').upload(fileName, file, { upsert: false })
    if (uploadError) { setError(`Upload failed: ${uploadError.message}`); setImageUrl(product?.image_url || null); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName)
    URL.revokeObjectURL(localPreview)
    setImageUrl(publicUrl)
    if (product?.id) {
      await supabase.from('products').update({ image_url: publicUrl }).eq('id', product.id)
    }
    setUploading(false)
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) { setError('Product name is required'); return }
    if (!formData.price || isNaN(formData.price)) { setError('Valid price is required'); return }
    setSaving(true); setError(null)

    // Total stock = sum of size stocks if sizes exist, otherwise use manual stock field
    const totalStock = sizes.length > 0
      ? sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0)
      : Number(formData.stock) || 0

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      price: Number(formData.price),
      stock: totalStock,
      available: formData.available,
      category_id: formData.category_id || null,
      type_id: formData.type_id || null,
      image_url: imageUrl || null,
      sizes: sizes.length > 0 ? sizes.map(s => ({ ...s, stock: Number(s.stock) || 0 })) : [],
    }

    const { error } = product
      ? await supabase.from('products').update(payload).eq('id', product.id)
      : await supabase.from('products').insert({ ...payload, currency: 'ZMW' })

    if (error) { setError(error.message); setSaving(false); return }
    setSaving(false)
    onClose()
  }

  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 w-full max-w-lg overflow-y-auto max-h-full">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200 dark:border-white/10">
          <div>
            <h2 className="font-black text-lg uppercase tracking-tight text-gray-900 dark:text-white">
              {product ? 'Edit Product' : 'Add Product'}
            </h2>
            <p className="text-gray-400 text-xs mt-0.5">
              {product ? 'Update product details and image' : 'Add a new product to the shop'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-xl leading-none">✕</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-5">

          {/* Image Upload */}
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Product Image</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {imageUrl
                  ? <img src={imageUrl} alt="Product" className="w-full h-full object-cover" />
                  : <span className="text-gray-400 text-xs text-center px-1">No image</span>
                }
              </div>
              <div className="flex flex-col gap-2">
                <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors w-fit">
                  {uploading ? 'Uploading...' : imageUrl ? 'Change Image' : 'Upload Image'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" />
                </label>
                <p className="text-gray-400 text-xs">PNG or JPG — max 3MB</p>
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>Product Name</label>
            <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. MWFC Home Jersey 2025/26" required className={inputClass} />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>Description</label>
            <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Brief product description..." rows={2} className={inputClass + ' resize-none'} />
          </div>

          {/* Price */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>Price (ZMW)</label>
            <input type="number" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="0.00" min="0" step="0.01" required className={inputClass} />
          </div>

          {/* ── Sizes ── */}
          <div className="flex flex-col gap-3 border-t border-gray-200 dark:border-white/10 pt-4">
            <div>
              <label className={labelClass}>Sizes & Stock</label>
              <p className="text-gray-400 text-xs mt-1">Toggle preset sizes or add custom ones. Set stock per size. If no sizes added, use the stock field below.</p>
            </div>

            {/* Preset size toggles */}
            <div className="flex flex-wrap gap-2">
              {PRESET_SIZES.map(size => (
                <button
                  key={size}
                  type="button"
                  onClick={() => togglePresetSize(size)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-colors ${
                    hasSize(size)
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-green-500 hover:text-green-600'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>

            {/* Custom size input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={customSize}
                onChange={e => setCustomSize(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomSize())}
                placeholder="Custom size e.g. 42 or 30x32"
                className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors flex-1"
              />
              <button
                type="button"
                onClick={addCustomSize}
                disabled={!customSize.trim()}
                className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 disabled:opacity-40 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
              >
                Add
              </button>
            </div>

            {/* Active sizes with stock input */}
            {sizes.length > 0 && (
              <div className="flex flex-col gap-2">
                {sizes.map(s => (
                  <div key={s.size} className="flex items-center gap-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2">
                    <span className="text-sm font-black text-gray-900 dark:text-white uppercase w-12 shrink-0">{s.size}</span>
                    <div className="flex items-center gap-2 flex-1">
                      <label className="text-xs text-gray-500 shrink-0">Stock:</label>
                      <input
                        type="number"
                        value={s.stock}
                        onChange={e => updateSizeStock(s.size, e.target.value)}
                        min="0"
                        className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-2 py-1 text-sm outline-none transition-colors w-20"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSize(s.size)}
                      className="text-red-500 hover:text-red-400 text-xs font-bold uppercase tracking-widest transition-colors shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <p className="text-xs text-gray-400">
                  Total stock: {sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0)} units across {sizes.length} size{sizes.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Manual stock — only shown when no sizes */}
            {sizes.length === 0 && (
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Stock (no sizes)</label>
                <input type="number" value={formData.stock} onChange={e => setFormData({ ...formData, stock: e.target.value })} placeholder="0" min="0" className={inputClass} />
              </div>
            )}
          </div>

          {/* Category and Type */}
          <div className="grid grid-cols-2 gap-3 border-t border-gray-200 dark:border-white/10 pt-4">
            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>Category</label>
              <select value={formData.category_id} onChange={e => setFormData({ ...formData, category_id: e.target.value, type_id: '' })} className={inputClass}>
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>Item Type</label>
              <select value={formData.type_id} onChange={e => setFormData({ ...formData, type_id: e.target.value })} disabled={!formData.category_id || types.length === 0} className={inputClass + ' disabled:opacity-50 disabled:cursor-not-allowed'}>
                <option value="">{!formData.category_id ? 'Select category first' : types.length === 0 ? 'No types added' : 'Select type'}</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          {/* Available toggle */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-white/10">
            <div>
              <div className={labelClass}>Visible in Shop</div>
              <p className="text-xs text-gray-400 mt-0.5">Toggle off to hide without deleting</p>
            </div>
            <button type="button" onClick={() => setFormData({ ...formData, available: !formData.available })} className={`w-12 h-6 rounded-full transition-colors relative ${formData.available ? 'bg-green-600' : 'bg-gray-300 dark:bg-white/20'}`}>
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.available ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-white/10">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 py-3 font-bold uppercase tracking-widest text-sm transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving || uploading} className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">
              {saving ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}